import React, { useState, useEffect, useMemo } from "react";
import {
  Button, TextField, Menu, MenuItem,
  CircularProgress,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import { GridLegacy as Grid, Typography, Box } from "@mui/material"
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import Icon from "../../Assets/images/png/Service.png";
import ActionIcon from "../../Assets/images/png/Action.png";
import { GetApiMethod, PostApiMethod, PatchApiMethod, DeleteApiMethod } from "../../Utils/ApiService";
import { Pagination } from "@mui/material";
import { ServiceEndpoint } from "../../Api/Api";
import SearchandExport from "../../Components/Common/SearchandExport/SearchandExport";
import BoxHeader from "../../Components/Common/Box/Boxheader";
import { useNavigate, useParams } from "react-router-dom";
import { useLocation, useSearchParams } from "react-router-dom";
import SuccessDialog from "../../Components/Common/SuccesMessages/SuccessDialog";
import { serviceAddonSchema } from "../../Components/Validation"; // Reuse validation
import { useSuccessDialog } from "../../Components/Common/SuccesMessages/SuccessDialog";
const textFieldStyle = {
  fontFamily: "Poppins, Regular",
  fontSize: "14px",
  width: 400,
  "& .MuiInputBase-root": {
    fontFamily: "Poppins, Regular",
    fontSize: "14px",
  },
};

interface Service {
  id: number;
  name: string;
  type?: string;
  status?: number;
}

const serviceTypes = ["Services", "Addons"];
const Service: React.FC = () => {
  // --- States for Services ---
  const [Services, setServices] = useState<Service[]>([]);
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);
  const [editingServiceId, setEditingServiceId] = useState<number | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchParams] = useSearchParams();
  // --- States for Addons ---
  const [addons, setAddons] = useState<Service[]>([]);
  const [filteredAddons, setFilteredAddons] = useState<Service[]>([]);
  const [editingAddonId, setEditingAddonId] = useState<number | null>(null);
  const [selectedAddonId, setSelectedAddonId] = useState<number | null>(null);
  const [anchorElAddon, setAnchorElAddon] = useState<null | HTMLElement>(null);
  const { id } = useParams<{ id: string }>();
  const [, setEditingSubCategoryId] = useState<string | null>(null);
  // --- Shared UI State ---
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showServiceList, setShowServiceList] = useState<boolean>(true);
  const [showAddonList, setShowAddonList] = useState<boolean>(false);
  const [isAddonForm, setIsAddonForm] = useState<boolean>(false);
  const [newServiceName, setNewServiceName] = useState<string>("");
  const [newServiceType, setNewServiceType] = useState<string>(serviceTypes[0]);
  const [newAddonName, setNewAddonName] = useState<string>("");
  const [newAddonType, setNewAddonType] = useState<string>(serviceTypes[0]);
  const [activeTab, setActiveTab] = useState<"services" | "addons">("services");
  const location = useLocation();
  const [, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [addonCurrentPage, setAddonCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);
  const tabs = ["All Services", "All Addons"];
  const navigate = useNavigate();
  const paginatedServices = filteredServices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const { open: successOpen, message, showSuccess, closeSuccess } = useSuccessDialog();
  const [errors, setErrors] = useState<{ name?: string; type?: string }>({});
  const paginatedAddons = useMemo(() => {
    const startIndex = (addonCurrentPage - 1) * itemsPerPage;
    return filteredAddons.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAddons, addonCurrentPage]);
  const selectedService = Services.find(s => s.id === selectedServiceId)
  const selectedAddon = addons.find(a => a.id === selectedAddonId);
  const addonTotalPages = Math.ceil(filteredAddons.length / itemsPerPage);
  const isMounted = React.useRef(false);
 useEffect(() => {
  if (!isMounted.current) {
    fetchServices();
    fetchAddons();
    isMounted.current = true;
  }
}, []);
  const handleNewButtonClick = () => {
    resetForm();
    setIsAddonForm(showAddonList);
    setShowServiceList(false);
    navigate(`/services/add`);
  };
  useEffect(() => {
    const typeParam = searchParams.get("type");
    if (location.pathname.endsWith("/add")) {
      setShowServiceList(false);
      setShowAddonList(false);
      if (typeParam === "services" || typeParam === "addons") {
        setActiveTab(typeParam);
      }
      resetForm();
    } else if (id) {
      setEditingSubCategoryId(id);
      setShowServiceList(false);
      setShowAddonList(false);
    } else {
      setShowServiceList(activeTab === "services");
      setShowAddonList(activeTab === "addons");
    }
  }, [location.pathname, id, searchParams]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await GetApiMethod(ServiceEndpoint.SERVICES_LIST_URL);
      const records = response?.data?.data?.records;
      if (Array.isArray(records)) {
        const servicesData = records.map((item: any) => ({
          id: item.id,
          name: item.name,
          status: item.status,
        }));
        setServices(servicesData);
        setFilteredServices(servicesData);
        console.log("Mapped Services:", servicesData);
      } else {
        console.warn("No records array found in response.");
      }
    } catch (error) {
      console.error("Failed to fetch services:", error);
    } finally {
      setLoading(false)
    }
  };

  const fetchAddons = async () => {
    setLoading(false)
    try {
      const response = await GetApiMethod(ServiceEndpoint.ADDONS_LIST_URL);
      const records = response?.data?.data?.records;
      if (Array.isArray(records)) {
        const addonData = records.map((item: any) => ({
          id: item.id,
          name: item.name,
          type: item.type,
          status: item.status,
        }));
        setAddons(addonData);
        setFilteredAddons(addonData);
      }
    } catch (error) {
      console.error("Failed to fetch addons:", error);
    } finally {
      setLoading(false)
    }
  };
  useEffect(() => {
    setLoading(true);
    setTimeout(() => setLoading(false), 2000);
  }, []);
  useEffect(() => {
    setFilteredServices(
      Services.filter((s) =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, Services]);

  useEffect(() => {
    setCurrentPage(1);
    setFilteredAddons(
      addons.filter((a) =>
        a.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, addons]);

  // --- Add/Edit Service ---
  const handleAddService = async () => {
    setTouched(true);
    if (newServiceName.trim() === "" || newServiceType.trim() === "") return;

    setLoading(true);
    try {
      if (editingServiceId !== null) {
        await PatchApiMethod(
          `${ServiceEndpoint.SERVICE_UPDATE}${editingServiceId}`,
          { name: newServiceName, type: newServiceType, status: 1 }
        );
        showSuccess("Services Updated successfully!");
      } else {
        await PostApiMethod(ServiceEndpoint.SERVICE_CREATE, {
          name: newServiceName,
          type: newServiceType,
        });
        showSuccess("Services Added successfully!");
      }
      // Refresh the correct list
      if (newServiceType === "Services") {
        await fetchServices();
        setShowServiceList(true);
        setShowAddonList(false);
      } else {
        await fetchAddons();
        setShowServiceList(false);
        // setShowAddonList(true);
      }
      resetForm();
      setTouched(false);
      // Close form and reset URL
      navigate("/services");
    } catch (error) {
      console.error("Failed to add/update service:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Add/Edit Addon ---
  const handleAddAddon = async () => {
    setTouched(true);
    if (newAddonName.trim() === "" || newAddonType.trim() === "") return;
    setLoading(true);
    try {
      if (editingAddonId !== null) {
        await PatchApiMethod(
          `${ServiceEndpoint.ADDONS_UPDATE}${editingAddonId}`,
          { name: newAddonName, type: newAddonType, status: 1 }
        );
        showSuccess("Addons Updated successfully!");
      } else {
        await PostApiMethod(ServiceEndpoint.SERVICE_CREATE, {
          name: newAddonName,
          type: newAddonType,
        });
        showSuccess("Addons Added successfully!");
      }
      if (newAddonType.toLowerCase() === "addons") {
        await fetchAddons();
        setShowAddonList(true);
        setShowServiceList(false);
      } else {
        await fetchServices();
        setShowAddonList(false);
        setShowServiceList(true);
      }
      resetForm();
      setTouched(false);
      // Close form and reset URL
      navigate("/services");
    } catch (error) {
      console.error("Failed to add/update addon:", error);
    } finally {
      setTouched(false);
      setLoading(false);
    }
  };

  // --- Delete handlers ---
  const handleDelete = async (type: "service" | "addon", id: number) => {
    try {
      // same endpoint for both
      const endpoint = `service/delete/${id}`;
      await DeleteApiMethod(endpoint);
      // refresh respective list
      if (type === "service") {
        fetchServices();
      } else {
        fetchAddons();
      }
      showSuccess(`${type} Deleted successfully!`);
    } catch (error) {
      console.error(`Failed to delete ${type}:`, error);
    }
  };

  // --- Edit handlers ---
  const handleEdit = (s: Service) => {
    setEditingServiceId(s.id);
    setNewServiceName(s.name);
    setIsAddonForm(false);
    setShowServiceList(false);
    handleCloseMenu();
  };
  const handleEditAddon = (a: Service) => {
    setEditingAddonId(a.id);
    setNewAddonName(a.name);
    setNewAddonType((a as any).type); // <-- add this
    setIsAddonForm(true);
    setShowAddonList(false);
    handleCloseAddonMenu();

    navigate(`/services/${a.id}/Edit`);
  };

  const handleCancel = () => {
    resetForm();
    setShowAddonList(isAddonForm);
    setShowServiceList(!isAddonForm);
    setTouched(false)
    navigate(`/services`);
  };

  // --- Menu Open/Close ---
  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(e.currentTarget);
    setSelectedServiceId(id);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  const handleOpenAddonMenu = (
    e: React.MouseEvent<HTMLElement>,
    id: number
  ) => {
    setAnchorElAddon(e.currentTarget);
    setSelectedAddonId(id);
  };
  const handleCloseAddonMenu = () => {
    setAnchorElAddon(null);
  };

  const resetForm = () => {
    setEditingServiceId(null);
    setEditingAddonId(null);
    setNewServiceName("");
    setNewServiceType(serviceTypes[0]);
    setNewAddonName("");
    setNewAddonType(serviceTypes[0]);
  };
  const handleStatusToggle1 = async (servicesId: number) => {
    try {
      const service = Services.find((s) => s.id === servicesId);
      if (!service) return;
      const newStatus = service.status ? 0 : 1;
      await PatchApiMethod(`${ServiceEndpoint.SERVICE_UPDATE}${servicesId}`, {
        status: newStatus,
      });
      showSuccess(
        `Service "${service.name}" ${newStatus ? "enabled" : "disabled"} successfully!`
      );
      await fetchServices(); // refresh list
    } catch (err) {
      console.error("Error updating service status:", err);
      showSuccess("Failed to update service status.");
    } finally {
      handleCloseMenu();
    }
  };

  const handleStatusToggle = async (addonId: number) => {
    try {
      const addon = addons.find(a => a.id === addonId);
      if (!addon) return;
      const newStatus = addon.status ? 0 : 1;
      await PatchApiMethod(`${ServiceEndpoint.ADDONS_UPDATE}${addon.id}`, {
        status: newStatus,
      });
      showSuccess(
        `Addon "${addon.name}" ${newStatus ? "enabled" : "disabled"} successfully!`
      );
      await fetchAddons(); // refresh list
    } catch (err) {
      console.error("Error updating addon status:", err);
      showSuccess("Failed to update addon status.");
    } finally {
      handleCloseAddonMenu();
    }
  };

  return (
    <AuthorizedLayout>
      <Box sx={{ px: 2, py: 3, }}>
        {loading && (
          <Box className="loading-overlay">
            <CircularProgress size={50} thickness={5} />
          </Box>
        )}
        <Card className="card-layout">
          <BoxHeader
            dashboard_name="Services & Addons"
            image={Icon}
            button_show={true}
            button_name="New Services & Addons"
            onAdd={handleNewButtonClick}
            tabs={tabs}
            activeTab={activeTab === "services" ? 0 : 1}
            onTabChange={(newTabIndex) => {
              resetForm();
              const tabKey = newTabIndex === 0 ? "services" : "addons";
              setActiveTab(tabKey);
              setShowServiceList(tabKey === "services");
              setShowAddonList(tabKey === "addons");
            }}
          />
          {!showServiceList && !showAddonList && (
            <Box>
              <Typography className="form-header">
                {editingServiceId || editingAddonId
                  ? "Edit Services & Addons Details"
                  : "Add New Services & Addons"}
              </Typography>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();

                  // Determine current values
                  const values = isAddonForm
                    ? { name: newAddonName, type: newAddonType }
                    : { name: newServiceName, type: newServiceType };

                  try {
                    // Validate using Yup schema
                    await serviceAddonSchema.validate(values, { abortEarly: false });
                    setErrors({}); // clear previous errors

                    // Call respective API
                    isAddonForm ? await handleAddAddon() : await handleAddService();

                  } catch (err: any) {
                    // Collect errors
                    const formErrors: any = {};
                    if (err.inner) {
                      err.inner.forEach((e: any) => {
                        formErrors[e.path] = e.message;
                      });
                    }
                    setErrors(formErrors);
                  }
                }}
              >
                <Box mt={2}>
                  <Grid container spacing={4} alignItems="center">
                    {/* Name Field */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="form-input" mb={1}>Name</Typography>
                      <TextField
                        className="form-input"
                        variant="standard"
                        placeholder="Type here"
                        fullWidth
                        value={isAddonForm ? newAddonName : newServiceName}
                        onChange={(e) =>
                          isAddonForm
                            ? setNewAddonName(e.target.value)
                            : setNewServiceName(e.target.value)
                        }
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                        InputProps={{ disableUnderline: false }}
                        inputProps={{ maxLength: 255 }}
                        sx={textFieldStyle}
                      />
                    </Grid>

                    {/* Type Field */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="form-input" mb={1}>Type</Typography>
                      <TextField
                        className="form-input"
                        select
                        variant="standard"
                        fullWidth
                        value={isAddonForm ? newAddonType : newServiceType}
                        onChange={(e) =>
                          isAddonForm
                            ? setNewAddonType(e.target.value)
                            : setNewServiceType(e.target.value)
                        }
                        error={Boolean(errors.type)}
                        helperText={errors.type}
                        InputProps={{ disableUnderline: false }}
                        sx={textFieldStyle}
                      >
                        {serviceTypes.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Box>

                {/* Action Buttons */}
                <Box mt={2} display="flex" gap={2}>
                  <Button onClick={handleCancel} className="btn-cancel" type="button">
                    Cancel
                  </Button>
                  <Button variant="contained" className="btn-next" type="submit">
                    Save
                  </Button>
                </Box>
              </form>
            </Box>

          )}
          {showAddonList && (
            <>
              <SearchandExport
                searchPlaceholder="Type here"
                onSearchChange={setSearchQuery}
              />

              <Table className="table">
                <TableHead >
                  <TableRow className="table-border">
                    <TableCell>
                      <div className="inner-cell">
                        <Typography className="table-header">Addons</Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="inner-cell">
                        <Typography className="table-header">Action</Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedAddons.map((addon, index) => (
                    <TableRow
                      key={addon.id}
                      sx={{
                        backgroundColor:
                          ((currentPage - 1) * itemsPerPage + index) % 2 === 0 ? "#f8f9fa" : "#fff",
                        fontFamily: "Poppins, Regular",
                      }}
                    >
                      {/* Content cell (dim if disabled) */}
                      <TableCell>
                        <div
                          className="inner-cell"
                          style={{
                            opacity: addon.status === 0 ? 0.5 : 1,
                            transition: "opacity 0.3s ease",
                          }}
                        >
                          <Typography className="table-cell">{addon.name}</Typography>
                        </div>
                      </TableCell>

                      {/* Action cell (always fully visible) */}
                      <TableCell>
                        <div className="inner-cell">
                          <Box display="flex" alignItems="center">
                            <Typography className="table-cell action">Action</Typography>
                            <IconButton onClick={(e) => handleOpenAddonMenu(e, addon.id)}>
                              <img src={ActionIcon} alt="icon" />
                            </IconButton>
                          </Box>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}

                  {filteredAddons.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography className="centered-muted-text">
                          No addons found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

              </Table>


              {/* Edit Menu */}
              <Menu
                anchorEl={anchorElAddon}
                open={Boolean(anchorElAddon)}
                onClose={handleCloseAddonMenu}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {(() => {
                  const addon = addons.find((a) => a.id === selectedAddonId);
                  if (!addon) return null;

                  const isDisabled = addon.status === 0;

                  return (
                    <>
                      {/* Show Edit/Delete only when enabled */}
                      {!isDisabled && (
                        <>
                          <MenuItem
                            onClick={() => {
                              handleEditAddon(addon);
                              navigate(`/services/${addon.id}`);
                            }}
                            className="edit-menu-item"
                          >
                            Edit
                          </MenuItem>

                          <MenuItem
                            onClick={() => {
                              handleCloseAddonMenu();
                              if (selectedAddonId !== null) {
                                handleDelete("addon", selectedAddonId);
                              }
                            }}
                            className="delete-menu-item"
                          >
                            Delete
                          </MenuItem>
                        </>
                      )}

                      {/* Always show Enable/Disable */}
                      <MenuItem
                        onClick={() => {
                          if (selectedAddonId !== null) {
                            handleStatusToggle(selectedAddonId);
                          }
                        }}
                        className={
                          addon.status ? "disable-menu-item" : "enable-menu-item"
                        }
                      >
                        {addon.status ? "Disable" : "Enable"}
                      </MenuItem>
                    </>
                  );
                })()}
              </Menu>

              {/* Pagination */}
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={addonTotalPages}
                  page={addonCurrentPage}
                  onChange={(_, value) => {
                    setAddonCurrentPage(value);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  color="primary"
                />
              </Box>
            </>
          )}

          {showServiceList && (
            <>
              <SearchandExport
                searchPlaceholder="Type here"
                onSearchChange={setSearchQuery}
              />
              <Table className="table">
                <TableHead>
                  <TableRow className="table-border">
                    <TableCell>
                      <div className="inner-cell">
                        <Typography className="table-header">Services</Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="inner-cell">
                        <Typography className="table-header">Action</Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedServices.map((s, index) => (
                    <TableRow
                      key={s.id}
                      sx={{
                        backgroundColor:
                          ((currentPage - 1) * itemsPerPage + index) % 2 === 0
                            ? "#f8f9fa"
                            : "#fff",
                        fontFamily: "Poppins, Regular",
                      }}
                    >
                      {/* Content cell dimmed if disabled */}
                      <TableCell>
                        <div
                          className="inner-cell"
                          style={{
                            opacity: s.status === 0 ? 0.5 : 1,
                            transition: "opacity 0.3s ease",
                          }}
                        >
                          <Typography className="table-cell">{s.name}</Typography>
                        </div>
                      </TableCell>

                      {/* Action column stays fully visible */}
                      <TableCell>
                        <div className="inner-cell">
                          <Box display="flex" alignItems="center">
                            <Typography className="table-cell action">Action</Typography>
                            <IconButton onClick={(e) => handleOpenMenu(e, s.id)}>
                              <img src={ActionIcon} alt="icon" />
                            </IconButton>
                          </Box>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}

                  {filteredServices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography className="centered-muted-text">
                          No Services found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

              </Table>

              {/* Action Menu */}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {(() => {
                  const service = Services.find((s) => s.id === selectedServiceId);
                  if (!service) return null;

                  const isDisabled = service.status === 0;

                  return (
                    <>
                      {/* Only show Edit/Delete when service is enabled */}
                      {!isDisabled && (
                        <>
                          <MenuItem
                            onClick={() => {
                              handleEdit(service);
                              navigate(`/services/${service.id}`);
                            }}
                            className="edit-menu-item"
                          >
                            Edit
                          </MenuItem>

                          <MenuItem
                            onClick={() => {
                              handleCloseMenu();
                              if (selectedServiceId !== null) {
                                handleDelete("service", selectedServiceId);
                              }
                            }}
                            className="delete-menu-item"
                          >
                            Delete
                          </MenuItem>
                        </>
                      )}

                      {/* Always show Enable/Disable option */}
                      <MenuItem
                        onClick={() => selectedServiceId && handleStatusToggle1(selectedServiceId)}
                        className={
                          service.status ? "disable-menu-item" : "enable-menu-item"
                        }
                      >
                        {service.status ? "Disable" : "Enable"}
                      </MenuItem>
                    </>
                  );
                })()}
              </Menu>


              {/* Pagination */}
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, value) => {
                    setCurrentPage(value);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  color="primary"
                />
              </Box>
            </>
          )}

        </Card>
        <SuccessDialog open={successOpen} onClose={closeSuccess} message={message} />
      </Box>
    </AuthorizedLayout>
  );
};

export default Service;
